import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EntityTypeMappingsService } from 'classes-lib';
import { IEntityTypeOption } from 'interfaces-lib';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Namespace } from 'enums-lib';
import { Subscription } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';

@Component({
  selector: 'app-entity-type-dropdown',
  standalone: false,
  templateUrl: './entity-type-dropdown.component.html',
  styleUrl: './entity-type-dropdown.component.css',
})
export class EntityTypeDropdownComponent implements OnInit, OnChanges, OnDestroy {

  @Input() formGroup: FormGroup;
  @Input() entityType!: string;
  // for salesforce enabled entities
  @Input() allowedEntityTypes: string[] | undefined;

  entityTypeOptions: IEntityTypeOption[] = [];

  private subscriptions = new Subscription();

  constructor(
    private identityStorage: IdentityStorage,
    private entityTypeMappingsService: EntityTypeMappingsService
  ) {}

  ngOnInit(): void {
    this.initialiseTemplateEntityType();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const entityTypeChanged = changes['entityType'] && !changes['entityType'].firstChange;
    const allowedChanged = changes['allowedEntityTypes'] && !changes['allowedEntityTypes'].firstChange;

    if (!entityTypeChanged && !allowedChanged) {
      return;
    }

    const templateEntityTypeControl = this.formGroup?.get('templateEntityType');
    if (!templateEntityTypeControl) {
      return;
    }

    const currentValue = templateEntityTypeControl.value;

    this.subscriptions.add(
      this.entityTypeMappingsService.getEntityTypeOptions(this.entityType).subscribe((options) => {
        this.entityTypeOptions = this.filterByAllowed(options);

        const isStillValid = this.entityTypeOptions.some((opt) => opt.value === currentValue);

        if (!isStillValid) {
          templateEntityTypeControl.setValue(null);
          this.setDefaultValue();
        }
        this.updateDropdownEnabledState();
      })
    );
  }

  private initialiseTemplateEntityType(): void {
    if (!this.entityType || !this.formGroup) {
      return;
    }

    const templateEntityTypeControl = this.formGroup.get('templateEntityType');
    if (!templateEntityTypeControl) {
      console.warn('DocmergeTemplateSection: templateEntityType control not found');
      return;
    }

    this.subscriptions.add(
      this.entityTypeMappingsService.getEntityTypeOptions(this.entityType).subscribe((options) => {
        this.entityTypeOptions = this.filterByAllowed(options);

        if (!templateEntityTypeControl.value) {
          this.setDefaultValue();
        }

        this.updateDropdownEnabledState();
      })
    );
  }

  private filterByAllowed(options: IEntityTypeOption[]): IEntityTypeOption[] {
    if (!this.allowedEntityTypes) {
      return options;
    }
    return options.filter((opt) => this.allowedEntityTypes.includes(opt.value));
  }

  private setDefaultValue(): void {
    const templateEntityTypeControl = this.formGroup?.get('templateEntityType');
    if (!templateEntityTypeControl) {
      return;
    }

    const defaultOption = this.entityTypeOptions.find(opt => opt.isDefault);
    if (defaultOption) {
      templateEntityTypeControl.setValue(defaultOption.value);
    }
  }

  private updateDropdownEnabledState(): void {
    const templateEntityTypeControl = this.formGroup?.get('templateEntityType');
    if (!templateEntityTypeControl) {
      return;
    }

    if (this.entityTypeOptions.length < 1) {
      templateEntityTypeControl.disable();
    } else {
      templateEntityTypeControl.enable();
    }
  }
}
